import { useState } from "react";

// Third Party
import {
  createFileRoute,
  useNavigate,
  type NavigateOptions,
} from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import type { AxiosError } from "axios";
// helpers
import { useAccessPermission } from "@/helpers/acessPermission";

// UI
import NotPermission from "@/components/NotPermission";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import {
  TaxLiabilityPlan,
  DividendPlan,
  type TaxObligationResponse,
  type DividendResponse,
} from "@/components/tables/TableTaxObligations";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
} from "@/components/ui/breadcrumb";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

// Services
import {
  // getTax,
  getDividends,
  getTaxSummary,
} from "@/services/tax-obligations";
import { clearLocalStorage } from "@/helpers/accessLocalStorage";
import { ErrorResponse } from "@/types/ErrorResponse";

export const Route = createFileRoute(
  "/_layout-private/report/tax-obligations/"
)({
  component: RouteComponent,
  validateSearch: (search: Record<string, unknown>) => {
    return {
      active: search.active as Tab,
      startYear: search.startYear as number,
      endYear: search.endYear as number,
    };
  },
});

type Tab = "taxLiabilityPlan" | "dividendPlan";

function RouteComponent() {
  const navigate = useNavigate();
  const {
    active,
    startYear: startYearParam,
    endYear: endYearParam,
  } = Route.useSearch();
  const id = String(localStorage.getItem("companyId"));
  const companyId = id.replace(/"/g, "");

  const initialZero = 0;
  // const currentYear = new Date().getFullYear();
  const [filter, setFilter] = useState({
    startYear: startYearParam || initialZero,
    endYear: endYearParam || initialZero,
  });

  const { delegationPlan } = useAccessPermission();
  const {
    data: getTaxData,
    isLoading,
    error: taxError,
  } = useQuery<TaxObligationResponse, AxiosError<ErrorResponse>>({
    queryKey: [
      "tax-obligations",
      companyId,
      active,
      filter.startYear,
      filter.endYear,
    ],
    queryFn: () =>
      getTaxSummary({
        companyId,
        skip: 0,
        limit: 100,
        startYear:
          filter.startYear === initialZero ? undefined : filter.startYear,
        endYear: filter.endYear === initialZero ? undefined : filter.endYear,
      }),
    enabled: !!companyId,
  });

  const {
    data: getDividendsData,
    isLoading: isLoadingDiv,
    error: dividendsError,
  } = useQuery<DividendResponse, AxiosError<ErrorResponse>>({
    queryKey: [
      "dividendPlan",
      companyId,
      active,
      filter.startYear,
      filter.endYear,
    ],
    queryFn: () =>
      getDividends({
        companyId,
        skip: 0,
        limit: 100,
        startYear:
          filter.startYear === initialZero ? undefined : filter.startYear,
        endYear: filter.endYear === initialZero ? undefined : filter.endYear,
      }),
    enabled: !!companyId,
  });

  if (taxError || dividendsError) {
    if (
      taxError?.response?.data?.message === "LOGGED_OUT" ||
      dividendsError?.response?.data?.message === "LOGGED_OUT"
    ) {
      console.log("clearing storage due to logged out");
      clearLocalStorage();
      window.location.reload();
    }
  }

  // Functions
  const handleStartYearChange = (value: string) => {
    const yearValue = Number(value);
    setFilter((prev) => ({ ...prev, startYear: yearValue }));

    if (filter.endYear !== initialZero && filter.endYear < yearValue) {
      setFilter((prev) => ({ ...prev, endYear: yearValue }));
      navigate({
        search: (prev) => ({
          ...prev,
          startYear: yearValue,
          endYear: yearValue,
        }),
      } as NavigateOptions);
    } else {
      navigate({
        search: (prev) => ({
          ...prev,
          startYear: yearValue,
        }),
      } as NavigateOptions);
    }
  };

  const handleEndYearChange = (value: string) => {
    const yearValue = Number(value);
    setFilter((prev) => ({ ...prev, endYear: yearValue }));
    navigate({
      search: (prev) => ({
        ...prev,
        endYear: yearValue,
      }),
    } as NavigateOptions);
  };

  // Define the tab change handler to update the query parameter
  const handleTabChange = (value: Tab) => {
    // setSearch({ tab: value });

    // console.log(value, "Tab");

    navigate({
      to: "/report/tax-obligations",
      search: {
        active: value,
        startYear: 0,
        endYear: 0,
      },
    });
  };

  return delegationPlan.isRead ? (
    <div>
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbPage>ລາຍງານແຜນມອບພັນທະອາກອນ</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>
      <Card className="mt-8">
        <CardHeader>
          <CardTitle>ລາຍງານແຜນມອບພັນທະໃຫ້ລັດ</CardTitle>
          <CardDescription>ລາຍການແຜນມອບພັນທະໃຫ້ລັດ</CardDescription>
        </CardHeader>

        <CardContent>
          {taxError && (
            <div className="text-red-500">
              Error fetching tax obligations: {taxError.message}
            </div>
          )}
          {dividendsError && (
            <div className="text-red-500">
              Error fetching dividends: {dividendsError.message}
            </div>
          )}
          <Tabs
            value={active || "taxLiabilityPlan"}
            defaultValue={active || "taxLiabilityPlan"}
            onValueChange={
              // (value) => console.log("Tab Value", value)
              (value) => handleTabChange(value as Tab)
            }
            className="flex flex-col gap-y-12"
          >
            <TabsList className="flex flex-wrap gap-2 justify-start items-start bg-transparent">
              <TabsTrigger
                value="taxLiabilityPlan"
                className="px-3 bg-gray-100  text-black data-[state=active]:bg-green-700 data-[state=active]:text-white"
              >
                ລາຍງານແຜນມອບພັນທະອາກອນ
              </TabsTrigger>
              <TabsTrigger
                value="dividendPlan"
                className="px-3 bg-gray-100  text-black data-[state=active]:bg-green-700 data-[state=active]:text-white"
              >
                ລາຍງານແຜນມອບເງິນປັນຜົນ
              </TabsTrigger>
            </TabsList>
            <TabsContent value="taxLiabilityPlan">
              <TaxLiabilityPlan
                data={getTaxData}
                onStartYearChange={handleStartYearChange}
                onEndYearChange={handleEndYearChange}
                startYear={filter.startYear}
                endYear={filter.endYear}
                isloading={isLoading}
              />
            </TabsContent>
            <TabsContent value="dividendPlan">
              <DividendPlan
                data={getDividendsData}
                onStartYearChange={handleStartYearChange}
                onEndYearChange={handleEndYearChange}
                startYear={filter.startYear}
                endYear={filter.endYear}
                isloading={isLoadingDiv}
              />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  ) : (
    <NotPermission />
  );
}
